import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';

// Define the schema for transaction data
const transactionSchema = z.object({
  tournament_id: z.string().uuid(),
  type: z.enum(['income', 'expense']),
  category: z.string().min(1, { message: "Category is required" }),
  description: z.string().min(1, { message: "Description is required" }),
  amount: z.number().positive({ message: "Amount must be greater than 0" }),
  transaction_date: z.string().min(1, { message: "Transaction date is required" }),
  status: z.enum(['pending', 'completed', 'cancelled']),
  payment_method: z.string().optional(),
  reference_number: z.string().optional(),
  notes: z.string().optional()
});

type TransactionData = z.infer<typeof transactionSchema>;

interface TransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: {
    id: string;
    type: 'income' | 'expense';
    category: string;
    description: string;
    amount: number;
    transaction_date: string;
    status: 'pending' | 'completed' | 'cancelled';
    payment_method?: string;
    reference_number?: string;
    notes?: string;
  } | null;
  tournamentId: string;
  transactionType: 'income' | 'expense';
  onSuccess: () => void;
}

export default function TransactionModal({
  isOpen,
  onClose,
  transaction,
  tournamentId,
  transactionType,
  onSuccess
}: TransactionModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm<TransactionData>({
    resolver: zodResolver(transactionSchema),
    defaultValues: {
      tournament_id: tournamentId,
      type: transactionType,
      category: '',
      description: '',
      amount: 0,
      transaction_date: new Date().toISOString().split('T')[0],
      status: 'pending',
      payment_method: '',
      reference_number: '',
      notes: ''
    }
  });

  useEffect(() => {
    if (transaction) {
      // Set form values from transaction
      setValue('tournament_id', tournamentId);
      setValue('type', transaction.type);
      setValue('category', transaction.category);
      setValue('description', transaction.description);
      setValue('amount', transaction.amount);
      setValue('transaction_date', transaction.transaction_date);
      setValue('status', transaction.status);
      setValue('payment_method', transaction.payment_method || '');
      setValue('reference_number', transaction.reference_number || '');
      setValue('notes', transaction.notes || '');
    } else {
      // Reset form for new transaction
      reset({
        tournament_id: tournamentId,
        type: transactionType,
        category: '',
        description: '',
        amount: 0,
        transaction_date: new Date().toISOString().split('T')[0],
        status: 'pending',
        payment_method: '',
        reference_number: '',
        notes: ''
      });
    }
  }, [transaction, tournamentId, transactionType, reset, setValue]);

  const onSubmit = async (data: TransactionData) => {
    try {
      setIsSubmitting(true);
      setError('');

      if (transaction) {
        // Update existing transaction
        const { error: updateError } = await supabase
          .from('financial_transactions')
          .update({
            type: data.type,
            category: data.category,
            description: data.description,
            amount: data.amount,
            transaction_date: data.transaction_date,
            status: data.status,
            payment_method: data.payment_method,
            reference_number: data.reference_number,
            notes: data.notes,
            updated_at: new Date().toISOString()
          })
          .eq('id', transaction.id);
        
        if (updateError) throw updateError;
        toast.success('Transaction updated successfully');
      } else {
        // Create new transaction
        const { error: createError } = await supabase
          .from('financial_transactions')
          .insert([{
            tournament_id: data.tournament_id,
            type: data.type,
            category: data.category,
            description: data.description,
            amount: data.amount,
            transaction_date: data.transaction_date,
            status: data.status,
            payment_method: data.payment_method,
            reference_number: data.reference_number,
            notes: data.notes
          }]);
        
        if (createError) throw createError;
        toast.success('Transaction created successfully');
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Transaction submission error:', err);
      setError(err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryOptions = () => {
    if (transactionType === 'income') {
      return [
        { value: 'sponsorship', label: 'Sponsorship' },
        { value: 'registration', label: 'Registration Fees' },
        { value: 'merchandise', label: 'Merchandise Sales' },
        { value: 'ticket', label: 'Ticket Sales' },
        { value: 'donation', label: 'Donations' },
        { value: 'other', label: 'Other Income' }
      ];
    } else {
      return [
        { value: 'venue', label: 'Venue & Facilities' },
        { value: 'prize', label: 'Prize Money' },
        { value: 'marketing', label: 'Marketing & PR' },
        { value: 'operations', label: 'Staff & Operations' },
        { value: 'equipment', label: 'Equipment' },
        { value: 'travel', label: 'Travel & Accommodation' },
        { value: 'catering', label: 'Catering & Hospitality' },
        { value: 'other', label: 'Other Expenses' }
      ];
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="absolute right-0 top-0 pr-4 pt-4 z-10">
                  <button
                    type="button"
                    className="rounded-full bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="border-b border-gray-200 bg-golf-sand/30 px-4 py-5 sm:px-6">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {transaction ? 'Edit' : 'Add'} {transactionType === 'income' ? 'Revenue' : 'Expense'}
                    </Dialog.Title>
                  </div>

                  <div className="px-4 py-5 sm:p-6 space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-600">
                        <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <p className="text-sm">{error}</p>
                      </div>
                    )}

                    <input
                      type="hidden"
                      {...register('tournament_id')}
                      value={tournamentId}
                    />

                    <input
                      type="hidden"
                      {...register('type')}
                      value={transactionType}
                    />

                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      <div className="sm:col-span-3">
                        <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                          Category <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('category')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          >
                            <option value="">Select a category</option>
                            {getCategoryOptions().map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        {errors.category && (
                          <p className="mt-2 text-sm text-red-600">{errors.category.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                          Status <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <select
                            {...register('status')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          >
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                        {errors.status && (
                          <p className="mt-2 text-sm text-red-600">{errors.status.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                          Description <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            {...register('description')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.description && (
                          <p className="mt-2 text-sm text-red-600">{errors.description.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                          Amount (₦) <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="number"
                            step="0.01"
                            {...register('amount', { valueAsNumber: true })}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.amount && (
                          <p className="mt-2 text-sm text-red-600">{errors.amount.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="transaction_date" className="block text-sm font-medium text-gray-700">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="date"
                            {...register('transaction_date')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.transaction_date && (
                          <p className="mt-2 text-sm text-red-600">{errors.transaction_date.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700">
                          Payment Method
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            {...register('payment_method')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.payment_method && (
                          <p className="mt-2 text-sm text-red-600">{errors.payment_method.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-3">
                        <label htmlFor="reference_number" className="block text-sm font-medium text-gray-700">
                          Reference Number
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            {...register('reference_number')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.reference_number && (
                          <p className="mt-2 text-sm text-red-600">{errors.reference_number.message}</p>
                        )}
                      </div>

                      <div className="sm:col-span-6">
                        <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                          Notes
                        </label>
                        <div className="mt-1">
                          <textarea
                            rows={3}
                            {...register('notes')}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange sm:text-sm"
                          />
                        </div>
                        {errors.notes && (
                          <p className="mt-2 text-sm text-red-600">{errors.notes.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex w-full justify-center rounded-md border border-transparent bg-golf-orange px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-golf-orange/90 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    >
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}